import Link from 'next/link';
import { motion } from 'framer-motion';

/**
 * QuoteBuilderHero — hero for /quote-builder, with a CSS illustration of the
 * request form rather than a product screenshot.
 *
 * The illustration is deliberate, on two grounds:
 *  1. There is no capture of the live quote form, and the storefront capture
 *     shows "Add to Cart" — which this tool does not have. Using it would
 *     misrepresent the product.
 *  2. It reflows. At 375px it becomes a clean full-width stack of labelled
 *     fields, where a dense app capture shrinks to illegible text. If this is
 *     ever swapped for a real capture, keep the illustration for narrow
 *     viewports rather than replacing it outright.
 */

const FIELDS = [
  { label: 'Product', value: 'Shaker door', select: true },
  { label: 'Width', value: '23.5″' },
  { label: 'Height', value: '41″' },
  { label: 'Material', value: 'Walnut', select: true },
  { label: 'Panel', value: 'Flat, glass', select: true },
  { label: 'Finish', value: 'Matte', select: true },
  { label: 'Quantity', value: '14' },
];

const REASSURANCE = [
  'Capture product specifications',
  'Review quote requests',
  'Works with your existing website',
];

export function QuoteBuilderHero() {
  return (
    <section className='relative overflow-hidden px-6 pt-32 pb-16 md:pt-36 md:pb-24'>
      <div className='container relative z-10 mx-auto max-w-6xl'>
        <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className='mb-6 inline-block rounded-full border border-jade-deep/60 bg-jade-deep/20 px-4 py-1.5 font-sans text-[11px] uppercase tracking-[0.25em] text-jade-soft'>
              Quote Builder
            </span>

            <h1 className='font-display text-4xl font-extrabold leading-[1.05] text-white [text-wrap:balance] sm:text-5xl md:text-6xl'>
              Get quote requests you can{' '}
              <span className='text-jade-soft'>actually price.</span>
            </h1>

            <p className='mt-6 max-w-xl text-lg leading-relaxed text-muted'>
              A quote builder for custom manufacturers, fabricators, and
              shops selling made-to-order products.
            </p>
            <p className='mt-4 max-w-xl text-lg leading-relaxed text-muted'>
              Add it to your existing website so customers can select their
              product and enter the exact dimensions, materials, quantities, and
              options you need to prepare a quote.
            </p>

            <div className='mt-8 flex flex-wrap items-center gap-4'>
              <Link
                href='/demo'
                className='inline-flex items-center gap-2 rounded-full bg-jade-cta px-7 py-3.5 font-semibold text-obsidian transition-colors hover:bg-jade-cta-deep'
              >
                Book a demo
                <span aria-hidden='true'>→</span>
              </Link>
              <Link
                href='#how-it-works'
                className='inline-flex items-center gap-2 rounded-full border border-jade-deep/60 px-7 py-3.5 font-semibold text-jade-soft transition-colors hover:border-jade-soft hover:text-white'
              >
                See how it works
              </Link>
            </div>

            <ul className='mt-8 flex flex-wrap gap-2'>
              {REASSURANCE.map((item) => (
                <li
                  key={item}
                  className='rounded-full border border-jade-deep/40 px-3 py-1.5 text-sm text-jade-soft'
                >
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* CSS illustration of the request form. aria-hidden — it is a
              picture of a form, not a form; exposing fake fields to a screen
              reader would be worse than describing it once. */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div
              aria-hidden='true'
              className='mx-auto max-w-sm rounded-2xl border border-white/10 bg-surface/80 p-5 shadow-2xl lg:mr-0'
            >
              <div className='mb-4 flex items-center justify-between'>
                <span className='text-sm font-semibold text-white'>
                  Request a quote
                </span>
                <span className='text-[10px] uppercase tracking-[0.2em] text-muted'>
                  On your website
                </span>
              </div>

              <div className='space-y-2.5'>
                {FIELDS.map((field) => (
                  <div key={field.label}>
                    <span className='mb-1 block text-[10px] uppercase tracking-[0.18em] text-muted'>
                      {field.label}
                    </span>
                    <span className='flex items-center justify-between rounded-lg border border-line bg-obsidian px-3 py-2 text-sm text-white'>
                      {field.value}
                      {field.select ? (
                        <span className='text-[10px] text-muted'>▾</span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>

              <span className='mt-4 block rounded-full bg-jade-cta px-6 py-2.5 text-center text-sm font-semibold text-obsidian'>
                Request a quote
              </span>
            </div>
            <p className='sr-only'>
              Illustration of a quote request form with fields for product,
              width, height, material, panel, finish, and quantity.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default QuoteBuilderHero;
